import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { setCookie } from 'nookies';
import { Globe, KeyRound } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@gu-corp/gu-ui';

import { ThemeProvider } from '~/components/theme-provider';

const languageOptions = [
  { value: 'en', labelKey: 'english' },
  { value: 'ja', labelKey: 'japanese' },
] as const;

interface Props {
  children: React.ReactNode;
  title?: string;
  description?: string;
}

const SignatureLayout: React.FC<Props> = ({ children, title, description }) => {
  const router = useRouter();
  const { t } = useTranslation('common');

  const onLanguageChange = useCallback(
    (selectedLanguage: string) => {
      setCookie(null, 'NEXT_LOCALE', selectedLanguage);
      window.location.href = '/' + selectedLanguage + router.asPath;
    },
    [router],
  );

  return (
    <ThemeProvider>
      <div
        className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800"
        data-testid="signature-layout"
      >
        {/* Header */}
        <header className="flex items-center justify-between px-4 h-12 bg-background/80 backdrop-blur-sm border-b border-border">
          <div />
          <div className="flex items-center gap-1.5">
            <img className="h-5" src="/images/new_joc_icon.svg" alt="JOC" />
            <span className="text-sm font-medium text-foreground">JOC Club</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Globe className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-popover text-popover-foreground">
              {languageOptions.map((option) => (
                <DropdownMenuItem key={option.value} onClick={() => onLanguageChange(option.value)}>
                  {t(option.labelKey)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Main Content */}
        <div className="w-full flex items-center justify-center flex-1 p-4">
          <div className="w-full max-w-md">
            <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <CardHeader className="text-center pb-4">
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <KeyRound className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-xl">{title || t('signature_required')}</CardTitle>
                {description && <CardDescription>{description}</CardDescription>}
              </CardHeader>
              <CardContent className="space-y-4">{children}</CardContent>
            </Card>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default SignatureLayout;
